import { useEffect, useRef, useState } from "react";
import { api, type GreetingsList } from "./api.ts";

// Greetings tab.
//
// A greeting is a short audio clip that flow blocks can reference
// (Diffuser, Demander un numéro, Menu DTMF, Répondeur). This page is
// just the recording library — global activation, voicemail mode and
// post-playback routing are owned by the flow editor.

export function Greetings(props: { token: string }) {
  const [list, setList] = useState<GreetingsList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedDurationMs, setRecordedDurationMs] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [audioBlobs, setAudioBlobs] = useState<Record<string, string>>({});
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Mirror of the control plane's multipart fileSize limit.
  const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

  async function refresh(): Promise<void> {
    try {
      const data = await api.listGreetings(props.token);
      setList(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    void refresh();
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      Object.values(audioBlobs).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token]);

  async function startRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        setRecordedDurationMs(Date.now() - startedAtRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      startedAtRef.current = Date.now();
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      setError(null);
    } catch (err) {
      setError(`micro : ${(err as Error).message}`);
    }
  }

  function stopRecording(): void {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }

  function importFile(file: File): void {
    if (file.size > MAX_IMPORT_BYTES) {
      setError(`import : fichier trop volumineux (max ${Math.round(MAX_IMPORT_BYTES / 1024 / 1024)} Mo)`);
      return;
    }
    const url = URL.createObjectURL(file);
    setRecordedBlob(file);
    setRecordedUrl(url);
    setRecordedDurationMs(null);
    // Duration comes from the media metadata; some containers report
    // Infinity (streamed webm) — leave the badge empty in that case,
    // the server computes the real duration after transcode anyway.
    const probe = new Audio(url);
    probe.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(probe.duration)) {
        setRecordedDurationMs(Math.round(probe.duration * 1000));
      }
    });
    if (!name.trim()) setName(file.name.replace(/\.[^.]+$/, ""));
    setError(null);
  }

  function discardRecording(): void {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordedDurationMs(null);
    setName("");
  }

  async function upload(): Promise<void> {
    if (!recordedBlob || !name.trim()) return;
    setBusy(true);
    try {
      // The voicemail-mode arg is legacy; flow blocks now own that.
      // We still pass "default" to satisfy the existing API contract.
      await api.uploadGreeting(props.token, name.trim(), recordedBlob, "default");
      discardRecording();
      await refresh();
    } catch (err) {
      setError(`téléversement : ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    if (!confirm("Supprimer cette annonce ?")) return;
    setBusy(true);
    try {
      await api.deleteGreeting(props.token, id);
      await refresh();
    } catch (err) {
      setError(`suppression : ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function preview(id: string): Promise<void> {
    if (audioBlobs[id]) return;
    try {
      const blob = await api.fetchGreetingAudio(props.token, id);
      setAudioBlobs((m) => ({ ...m, [id]: URL.createObjectURL(blob) }));
    } catch (err) {
      setError(`écoute : ${(err as Error).message}`);
    }
  }

  return (
    <>
      <section className="card">
        <div className="card-header">
          <h3>Nouvelle annonce — micro ou fichier</h3>
          {recordedDurationMs != null && !recording && (
            <span className="muted">{Math.round(recordedDurationMs / 1000)} s</span>
          )}
        </div>
        <div className="dial" style={{ gap: "0.75rem" }}>
          <div className="row" style={{ gap: "0.5rem" }}>
            {!recording && !recordedBlob && (
              <>
                <button className="primary" onClick={startRecording} disabled={busy} type="button">
                  ● Démarrer l'enregistrement
                </button>
                <button
                  className="ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  type="button"
                >
                  ⬆ Importer un fichier audio
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm,.mp4"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) importFile(file);
                    e.target.value = "";
                  }}
                />
              </>
            )}
            {recording && (
              <button className="danger" onClick={stopRecording} type="button">
                ■ Arrêter
              </button>
            )}
            {recording && <span className="muted">Enregistrement…</span>}
            {recordedBlob && !recording && (
              <button className="ghost" onClick={discardRecording} disabled={busy} type="button">
                Annuler
              </button>
            )}
          </div>
          {recordedUrl && <audio src={recordedUrl} controls style={{ width: "100%" }} />}
          {recordedBlob && !recording && (
            <>
              <div className="row" style={{ gap: "0.5rem" }}>
                <input
                  placeholder="Nom (ex. Bureau fermé aujourd'hui)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{ flex: 1 }}
                  disabled={busy}
                />
              </div>
              <div className="row" style={{ gap: "0.5rem" }}>
                <button
                  className="primary"
                  onClick={upload}
                  disabled={busy || !name.trim()}
                  type="button"
                >
                  ⬆ Téléverser l'annonce
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h3>Annonces enregistrées</h3>
          <div className="actions">
            <button className="ghost" onClick={() => void refresh()} type="button">↻ Actualiser</button>
          </div>
        </div>
        {error && <div className="error">{error}</div>}
        {list == null && !error && <div className="empty">Chargement…</div>}
        {list != null && list.greetings.length === 0 && (
          <div className="empty">Aucune annonce — enregistrez-en ou importez-en une ci-dessus.</div>
        )}
        {list != null && list.greetings.length > 0 && (
          <table className="data">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Durée</th>
                <th>Aperçu</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.greetings.map((g) => (
                <tr key={g.id}>
                  <td><strong>{g.name}</strong></td>
                  <td>
                    {g.durationMs != null ? `${Math.round(g.durationMs / 1000)} s` : "—"}
                  </td>
                  <td>
                    {audioBlobs[g.id] ? (
                      <audio src={audioBlobs[g.id]} controls style={{ height: "1.6rem", verticalAlign: "middle" }} />
                    ) : (
                      <button className="ghost" onClick={() => preview(g.id)} type="button">
                        ▶ Écouter
                      </button>
                    )}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className="ghost"
                      onClick={() => remove(g.id)}
                      disabled={busy}
                      title="Supprimer"
                      type="button"
                    >
                      ✕ Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
